/**
 * ContractsList Component
 *
 * Contracts list with status/contractor filters, pagination, and navigation.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { Contract, ContractStatus } from '@/types/contractors';
import { CONTRACT_STATUS_CONFIG } from '@/types/contractors';
import { contractsApi } from '@/services/contractorsApi';

interface ContractsListProps {
  isAdmin: boolean;
}

const PAGE_SIZE = 20;
const STATUS_OPTIONS: ContractStatus[] = ['draft', 'active', 'expired', 'terminated', 'renewed'];

export function ContractsList({ isAdmin }: ContractsListProps): React.JSX.Element {
  const { t } = useTranslation('contractors');
  const navigate = useNavigate();

  const [contracts, setContracts] = useState<Contract[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [contractorFilter, setContractorFilter] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContracts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await contractsApi.list({
        status: statusFilter || undefined,
        search: contractorFilter || undefined,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      });
      setContracts(result);
      setTotal(result.length);
    } catch (err) {
      setError(t('errors.loadFailed'));
      console.error('Failed to load contracts:', err);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, contractorFilter, page, t]);

  useEffect(() => {
    void fetchContracts();
  }, [fetchContracts]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, contractorFilter]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const formatCurrency = (amount: number, currency: string = 'EUR'): string => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">{t('contracts.title')}</h2>
        {isAdmin && (
          <button
            onClick={() => navigate('/contractors/contracts/new')}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('contracts.add')}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">{t('contracts.allStatuses')}</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{t(`statuses.${s}`)}</option>
          ))}
        </select>
        {isAdmin && (
          <input
            type="text"
            value={contractorFilter}
            onChange={(e) => setContractorFilter(e.target.value)}
            placeholder={t('contracts.filterContractor')}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        )}
      </div>

      {error && <div className="p-3 text-sm text-red-700 bg-red-50 rounded-lg">{error}</div>}

      {/* Table */}
      <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('contracts.titleCol')}</th>
              {isAdmin && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('contracts.contractor')}</th>
              )}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('contracts.rateType')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('contracts.rateAmount')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('contracts.status')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('contracts.startDate')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('contracts.endDate')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={isAdmin ? 7 : 6} className="px-4 py-8 text-center text-sm text-gray-500">
                  {t('common.loading')}
                </td>
              </tr>
            ) : contracts.length === 0 ? (
              <tr>
                <td colSpan={isAdmin ? 7 : 6} className="px-4 py-8 text-center text-sm text-gray-500">
                  {t('contracts.noResults')}
                </td>
              </tr>
            ) : (
              contracts.map((contract) => {
                const statusCfg = CONTRACT_STATUS_CONFIG[contract.status];
                return (
                  <tr
                    key={contract.id}
                    onClick={() => navigate(`/contractors/contracts/${contract.id}`)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{contract.title}</td>
                    {isAdmin && (
                      <td className="px-4 py-3 text-sm text-gray-600">{contract.contractor_name || '-'}</td>
                    )}
                    <td className="px-4 py-3 text-sm text-gray-600 capitalize">{contract.rate_type.replace('_', ' ')}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(contract.rate_amount, contract.currency)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${statusCfg.bgColor} ${statusCfg.color}`}>
                        {t(`statuses.${contract.status}`)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDate(contract.start_date)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDate(contract.end_date)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">
            {t('common.showing', { from: (page - 1) * PAGE_SIZE + 1, to: Math.min(page * PAGE_SIZE, total), total })}
          </span>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
              {t('common.previous')}
            </button>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
              {t('common.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
